import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, ClipboardCheck, UserCheck, PartyPopper, Send, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Admissions = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Netlify Forms submission
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'enquiry',
          ...formData
        }).toString()
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
    
    setIsSubmitting(false);
  };

  const whatsappNumber = '919481694328';
  const whatsappMessage = `Hello! I'm interested in admissions at Sacred Heart School. My details:\nName: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nMessage: ${formData.message}`;
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const steps = [
    {
      icon: FileText,
      title: 'Enquiry',
      description: 'Fill out the enquiry form or contact us via WhatsApp to express your interest.',
      color: 'bg-gradient-to-br from-orange-400 to-amber-500',
    },
    {
      icon: ClipboardCheck,
      title: 'Documentation',
      description: 'Submit required documents including birth certificate and photos.',
      color: 'bg-gradient-to-br from-cyan-400 to-blue-500',
    },
    {
      icon: UserCheck,
      title: 'Interaction',
      description: 'Brief interaction with the child and parents to understand needs.',
      color: 'bg-gradient-to-br from-teal-400 to-green-500',
    },
    {
      icon: PartyPopper,
      title: 'Welcome!',
      description: 'Complete registration and welcome your child to our family!',
      color: 'bg-gradient-to-br from-pink-400 to-rose-500',
    },
  ];

  const documents = [
    'Birth Certificate (original + photocopy)',
    'Passport-size photos of child (3 copies)',
    'Aadhar Card of child (if available)',
    'Aadhar Card of parents (photocopy)',
    'Address proof (any government ID)',
    'Previous school TC (if applicable)',
  ];

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50" data-testid="admissions-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <span className="font-accent text-xl text-orange-500 inline-block -rotate-2 mb-2">
              Join Us
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-slate-800 tracking-wider mb-6">
              ADMISSIONS OPEN 2025-26
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Begin your child's educational journey with Sacred Heart School. We welcome children for our Nursery, LKG, and UKG programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-accent text-xl text-purple-500 inline-block -rotate-2 mb-2">
              How to Apply
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-slate-800 tracking-wider">
              SIMPLE ADMISSION PROCESS
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
                data-testid={`admission-step-${index + 1}`}
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-slate-200">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-300 rounded-full"></div>
                  </div>
                )}
                
                <div className="bg-slate-50 p-8 rounded-[2rem] text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:bg-white group">
                  <div className="relative inline-block mb-6">
                    <div className={`w-20 h-20 ${step.color} rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg`}>
                      <step.icon className="w-10 h-10 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md font-heading text-slate-700">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="font-heading text-xl text-slate-800 tracking-wider mb-3">
                    {step.title.toUpperCase()}
                  </h3>
                  <p className="font-body text-slate-600">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents & Enquiry Form */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-pink-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Required Documents */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-xl text-pink-500 inline-block -rotate-2 mb-2">
                Checklist
              </span>
              <h2 className="font-heading text-4xl text-slate-800 tracking-wider mb-8">
                REQUIRED DOCUMENTS
              </h2>
              
              <div className="bg-white p-8 rounded-[2rem] shadow-lg border border-slate-100">
                <ul className="space-y-4">
                  {documents.map((doc, index) => (
                    <li key={index} className="flex items-start gap-3" data-testid={`document-${index}`}>
                      <div className="w-6 h-6 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="font-body text-slate-700">{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Enquiry Form - Netlify Forms */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-xl text-cyan-500 inline-block -rotate-2 mb-2">
                Get in Touch
              </span>
              <h2 className="font-heading text-4xl text-slate-800 tracking-wider mb-8">
                ENQUIRY FORM
              </h2>

              <div className="bg-white p-8 rounded-[2rem] shadow-lg border border-slate-100">
                {isSubmitted ? (
                  <div className="text-center py-8" data-testid="form-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-heading text-2xl text-slate-800 tracking-wider mb-2">
                      THANK YOU!
                    </h3>
                    <p className="font-body text-slate-600">
                      We've received your enquiry and will contact you soon.
                    </p>
                  </div>
                ) : (
                  <form 
                    name="enquiry" 
                    method="POST" 
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit} 
                    className="space-y-6" 
                    data-testid="enquiry-form"
                  >
                    <input type="hidden" name="form-name" value="enquiry" />
                    <p className="hidden">
                      <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                    </p>
                    
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Parent's Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body transition-all"
                        placeholder="Enter your full name"
                        data-testid="input-name"
                      />
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body transition-all"
                        placeholder="Enter your phone number"
                        data-testid="input-phone"
                      />
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body transition-all"
                        placeholder="Enter your email address"
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-2">
                        Message
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body transition-all resize-none"
                        placeholder="Tell us about your child (program interested in, etc.)"
                        data-testid="input-message"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/30 active:translate-y-0 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                        data-testid="submit-enquiry"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Submit Enquiry
                          </>
                        )}
                      </Button>

                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-green-500/30 active:translate-y-0 active:scale-95"
                        data-testid="whatsapp-enquiry"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp Us
                      </a>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Info Banner */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-wider mb-4">
              HAVE QUESTIONS? WE'RE HERE TO HELP!
            </h2>
            <p className="font-body text-lg text-white/90 mb-6">
              Call us at <a href="tel:9481694328" className="underline font-bold">9481694328</a> or email <a href="mailto:sacredheartckm@gmail.com" className="underline font-bold">sacredheartckm@gmail.com</a>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
